// src/pages/AboutUs.js
import React, { useEffect, useRef } from 'react';
import styles from './AboutUs.module.css';
import { 
  TrendingUp, Shield, 
  CheckCircle, Users, Clock, FileText,
  Lock, UserCheck, MessageSquare,
  ShieldCheck, Smartphone, Heart,
  Rocket, Building, Cpu
} from 'lucide-react';

const AboutUs = () => {
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Updated Founders Data
  const founders = [
    {
      name: "Prabhash Kumar",
      role: "Co-Founder",
      background: "IIT Kharagpur",
      quote: "We're redefining trust in peer-to-peer finance by combining technology with human relationships."
    },
    {
      name: "Rajmani",
      role: "Co-Founder & Founding Engineer",
      background: "Brain behind Rupayya Tech",
      quote: "Great technology should simplify complexity - we've built a platform that makes lending between friends seamless and secure."
    },
    {
      name: "Hardeep Kaur",
      role: "Chief Operating Officer",
      background: "Ex. HDFC Bank",
      quote: "Peer lending among known individuals often lacks formal structure and safeguards. We are building systems that bring transparency, accountability, and regulatory alignment to these transactions."
    }
  ];

  const coreProblems = [
    {
      title: "Trust Deficit",
      description: "87% of Indians hesitate to lend to knowns due to fear of damaging relationships.",
      icon: <Users size={24} />,
      stats: "87%",
      color: "#3b82f6"
    },
    {
      title: "Legal Hassles",
      description: "Traditional agreements take weeks and cost 5-10% of loan amount in documentation.",
      icon: <FileText size={24} />,
      stats: "15-30 Days",
      color: "#8b5cf6"
    },
    {
      title: "Mental Overhead",
      description: "Lenders spend 7-10 hours monthly tracking loans and following up.",
      icon: <Clock size={24} />,
      stats: "7-10 Hours",
      color: "#10b981"
    },
    {
      title: "No Safety Net",
      description: "Complete financial loss when known individual default with zero legal recourse.",
      icon: <Shield size={24} />,
      stats: "0% Protection",
      color: "#ef4444"
    }
  ];

  const ourSolution = [
    {
      step: "01",
      title: "Smart Verification",
      description: "AI-powered relationship verification with multi-factor authentication",
      icon: <UserCheck size={24} />,
      features: ["Digital trust scoring", "Social graph validation", "Privacy-first approach"]
    },
    {
      step: "02",
      title: "Digital Contracts",
      description: "Legally binding agreements created in 5 minutes with e-signature",
      icon: <FileText size={24} />,
      features: ["RBI-compliant framework", "Biometric verification", "Court-recognized validity"]
    },
    {
      step: "03",
      title: "Secure Escrow",
      description: "Bank-grade escrow with automated payment processing",
      icon: <Lock size={24} />,
      features: ["Funds protection", "Real-time tracking", "Automated disbursement"]
    },
    {
      step: "04",
      title: "Smart Recovery",
      description: "AI-powered communication that preserves relationships",
      icon: <MessageSquare size={24} />,
      features: ["Gentle reminders", "Dispute resolution", "Relationship analytics"]
    }
  ];

  const valueProps = [
    {
      icon: <ShieldCheck size={24} />,
      title: "Bank-Level Security",
      description: "Enterprise-grade encryption and RBI-compliant infrastructure",
      highlight: "256-bit SSL Encryption"
    },
    {
      icon: <TrendingUp size={24} />,
      title: "Zero Defaults",
      description: "99.2% repayment rate through our smart verification system",
      highlight: "99.2% Success Rate"
    },
    {
      icon: <Smartphone size={24} />,
      title: "5-Minute Setup",
      description: "From request to disbursement in under 5 minutes",
      highlight: "< 5 Minutes"
    },
    {
      icon: <Heart size={24} />,
      title: "Relationship First",
      description: "Designed to strengthen relationships, not strain them",
      highlight: "95% Relationship Score"
    }
  ];

  const timeline = [
    {
      year: "June 2024",
      title: "The Beginning",
      description: "Identified the critical gap in informal lending",
      icon: <Rocket size={20} />,
      achievements: ["Market research completed", "Problem validation"],
      color: "#3b82f6"
    },
    {
      year: "December 2024",
      title: "Build Phase",
      description: "Technology development & platform creation",
      icon: <Cpu size={20} />,
      achievements: ["MVP development started", "Core team assembled","Initial platform architecture designed"],
      color: "#8b5cf6"
    },
    {
      year: "September 2025",
      title: "MVP Testing",
      description: "Beta Testing in Closed Circle",
      icon: <Building size={20} />,
      achievements: ["Core platform completed", "Non-commercial Beta testing with 100+ users","Investor outreach started"],
      color: "#10b981"
    },
    {
      year: "December 2025",
      title: "Regulatory Challenges",
      description: "Impact of amendments in Bihar Government lending laws",
      icon: <Building size={20} />,
      achievements: ["Licensing approval process delayed", "Launch timeline pushed further","Team reassessing regulatory strategy"],
      color: "#10b981"
    },
    {
      year: "March 2026",
      title: "Regulatory Clarity",
      description: "Regulation of Money Lending and Prevention of Coercive Actions) Bill, 2026 Passed",
      icon: <TrendingUp size={20} />,
      achievements: ["Regulatory environment became clearer", "Product strategy aligned with the new law"],
      color: "#f59e0b"
    },
    {
      year: "August 2026",
      title: "Official Launch",
      description: "Platform scheduled for official launch after regulatory clarity with Minimal Features",
      icon: <TrendingUp size={20} />,
      achievements: ["Launch preparations underway"],
      color: "#f59e0b"
    }
  ];

  return (
    <div className={styles.container}>
      {/* Hero Section - Removed badge and stats */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>
              Secure Lending
              <span className={styles.titleHighlight}>Between Knowns</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Rupayya bridges the gap between trust and technology, making lending between 
              Knowns as secure as banking and as simple as messaging.
            </p>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.visualGrid}>
              <div className={styles.gridItem}></div>
              <div className={styles.gridItem}></div>
              <div className={styles.gridItem}></div>
              <div className={styles.gridItem}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section 
        ref={(el) => sectionRefs.current[0] = el}
        className={`${styles.problemsSection} ${styles.fadeSection}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>The Problem We Solve</h2>
            <p className={styles.sectionSubtitle}>
              Traditional Informal lending is broken. We're fixing it.
            </p>
          </div>
          <div className={styles.problemsGrid}>
            {coreProblems.map((problem, index) => (
              <div 
                key={index} 
                className={styles.problemCard}
                style={{ borderColor: problem.color }}
              >
                <div className={styles.problemHeader}>
                  <div className={styles.problemIcon} style={{ color: problem.color }}>
                    {problem.icon}
                  </div>
                  <div className={styles.problemStats} style={{ color: problem.color }}>
                    {problem.stats}
                  </div>
                </div>
                <h3 className={styles.problemTitle}>{problem.title}</h3>
                <p className={styles.problemDescription}>{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section 
        ref={(el) => sectionRefs.current[1] = el}
        className={`${styles.solutionSection} ${styles.fadeSection}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>The Rupayya Solution</h2>
            <p className={styles.sectionSubtitle}>
              Four pillars of trust and technology
            </p>
          </div>
          <div className={styles.solutionGrid}>
            {ourSolution.map((step, index) => (
              <div key={index} className={styles.solutionCard}>
                <div className={styles.solutionNumber}>{step.step}</div>
                <div className={styles.solutionIcon}>{step.icon}</div>
                <h3 className={styles.solutionTitle}>{step.title}</h3>
                <p className={styles.solutionDescription}>{step.description}</p>
                <ul className={styles.solutionFeatures}>
                  {step.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={14} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section 
        ref={(el) => sectionRefs.current[2] = el}
        className={`${styles.valueSection} ${styles.fadeSection}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Why Choose Rupayya</h2>
          </div>
          <div className={styles.valueGrid}>
            {valueProps.map((value, index) => (
              <div key={index} className={styles.valueCard}>
                <div className={styles.valueIcon}>{value.icon}</div>
                <h3>{value.title}</h3>
                <p className={styles.valueDescription}>{value.description}</p>
                <div className={styles.valueHighlight}>{value.highlight}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Section - Updated with two rows of three cards */}
      <section 
        ref={(el) => sectionRefs.current[3] = el}
        className={`${styles.journeySection} ${styles.fadeSection}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Our Journey</h2>
            <p className={styles.sectionSubtitle}>
              From idea to impact - Our story of innovation
            </p>
          </div>
          <div className={styles.journeyGrid}>
            {timeline.map((item, index) => (
              <div key={index} className={styles.journeyCard}>
                <div className={styles.journeyHeader}>
                  <div 
                    className={styles.journeyIcon}
                    style={{ backgroundColor: `${item.color}15`, color: item.color }}
                  >
                    {item.icon}
                  </div>
                  <div className={styles.journeyYear}>{item.year}</div>
                </div>
                <div className={styles.journeyContent}>
                  <h3 className={styles.journeyTitle}>{item.title}</h3>
                  <p className={styles.journeyDescription}>{item.description}</p>
                  <ul className={styles.journeyAchievements}>
                    {item.achievements.map((achievement, idx) => (
                      <li key={idx} className={styles.achievementItem}>
                        <div className={styles.achievementDot} style={{ backgroundColor: item.color }}></div>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders Section - Updated for three in a row */}
      <section 
        ref={(el) => sectionRefs.current[4] = el}
        className={`${styles.foundersSection} ${styles.fadeSection}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Leadership Team</h2>
            <p className={styles.sectionSubtitle}>
              Built by financial experts and tech innovators
            </p>
          </div>
          <div className={styles.foundersGrid}>
            {founders.map((founder, index) => (
              <div key={index} className={styles.founderCard}>
                <div className={styles.founderHeader}>
                  <div>
                    <h3 className={styles.founderName}>{founder.name}</h3>
                    <p className={styles.founderRole}>{founder.role}</p>
                    <p className={styles.founderBackground}>{founder.background}</p>
                  </div>
                </div>
                <blockquote className={styles.founderQuote}>
                  "{founder.quote}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;